import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Settings, RefreshCw, Sliders, Wifi, WifiOff } from 'lucide-react';
import { isPlatformAdmin } from '../../utils/roles';
import { telemetryService, deviceService } from '../../services';
import thingsBoardWebSocket from '../../services/websocket';
import './ProductionDashboard.css';

// The only server attributes this dashboard owns. Everything else living in
// SERVER_SCOPE -- eff_state (the rule chain's live slot/state accumulator),
// eff_hour_*, shift_config -- belongs to the AquaSew rule chain or the
// ThingsBoard shift widget. Reading those into this form, or writing them back
// from it, rewinds the running totals and copies one machine's shift onto
// another.
// NOTHING INVENTED MAY LIVE HERE. This object is the initial state AND what the
// settings modal writes back, so every sample value in it gets saved onto the
// device as if an operator had typed it. Measured 2026-09-11: targetEfficiency
// 80, plannedOperators 20 and smv 5.8 were identical on 17 of 17 configured
// machines -- nobody had ever set them. A cutter and a bartack do not share an
// SMV; that is the placeholder, not data. (Same failure as the eff_state /
// shift_config write-back in PR #14, and the 'T-SHIRT MODEL X' task in PR #38.)
const DEFAULT_ATTRIBUTES = {
  targetEfficiency: '',
  plannedOperators: '',
  smv: '',
  // Empty, NOT a sample style. This object is the initial state AND what the
  // settings modal writes back, so any placeholder parked here gets saved onto
  // the device as if it were real: 'T-SHIRT MODEL X' reached the task attribute
  // of 6 live machines that way. Same failure as the eff_state/shift_config
  // write-back in PR #14 -- nothing invented may sit in here.
  task: '',
  operations: '',
  // How the hourly target is defined: 'smv' (derive from SMV x operators)
  // or 'direct' (operator types pieces/hour). Read by the AquaSew rule chain,
  // which computes the efficiency telemetry key from it.
  target_mode: 'smv',
  target_per_hour: 0
};
const OWNED_ATTRIBUTE_KEYS = Object.keys(DEFAULT_ATTRIBUTES);

// Bucket widths, matching the server's intervalMap.
const INTERVAL_MS = {
  '15min': 15 * 60 * 1000,
  '30min': 30 * 60 * 1000,
  hourly: 60 * 60 * 1000,
  daily: 24 * 60 * 60 * 1000,
  weekly: 7 * 24 * 60 * 60 * 1000,
  monthly: 30 * 24 * 60 * 60 * 1000
};
// Resolutions that can sensibly follow a shift. Anything longer spans days, and
// a single anchor cannot describe days whose shifts started at different times.
const INTRA_SHIFT_INTERVALS = ['15min', '30min', 'hourly'];

const ProductionDashboard = ({ dashboard: dashboardProp, selectedDevice }) => {
  const [dashboard, setDashboard] = useState(dashboardProp);
  const [timeInterval, setTimeInterval] = useState('hourly');
  const [telemetryData, setTelemetryData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showParamMapping, setShowParamMapping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dataError, setDataError] = useState('');
  // Shift total taken from history, not from a running accumulator: the engine
  // can only count messages it has seen, so a mid-shift deploy or restart would
  // under-report the shift (37 instead of 4220 when this was first shipped).
  const [shiftTotal, setShiftTotal] = useState(null);
  // Pieces per hour of the current shift, keyed by hour offset from shift start
  // (0 = H1). Comes free with the shift-total query, which already asks for
  // hourly buckets and then sums them.
  const [shiftHours, setShiftHours] = useState(null);
  const [websocketConnected, setWebsocketConnected] = useState(false);
  const pollingIntervalRef = useRef(null);
  const websocketInitializedRef = useRef(false);
  // Which device a chart request is in flight for, or null. A boolean here used
  // to drop the request issued when the machine changed, because it could not
  // tell "same device, duplicate poll" from "new selection".
  const chartLoadingRef = useRef(null);
  // Timers and the telemetry handler created while setting up the websocket.
  // They live in refs so the effect that created them can tear them down: they
  // used to be cleaned up by a function returned from initializeWebSocket,
  // which nobody ever called, so every machine you visited left a timer behind
  // polling it forever.
  const chartRefreshIntervalRef = useRef(null);
  const statusCheckIntervalRef = useRef(null);
  const wsErrorCheckIntervalRef = useRef(null);
  const telemetryHandlerRef = useRef(null);
  // Always holds the currently selected device, so a response that arrives
  // after the operator has moved on can be recognised and dropped.
  const deviceIdRef = useRef(null);
  
  // Date range for chart
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  // While true the window's end follows the clock, so new buckets appear
  // without a reload. Editing either date pins the range: it is the operator's
  // then, and must not move under them.
  const [rangeFollowsClock, setRangeFollowsClock] = useState(true);
  
  // Server attributes form
  const [attributes, setAttributes] = useState(DEFAULT_ATTRIBUTES);

  // The open manifest, read from SHARED_SCOPE purely to caption the SESSION
  // TOTAL card. Style is not on the wire -- the device echoes manifest_id and
  // session_id in telemetry but never style_id/style_name -- so the block has
  // to be read directly.
  // DELIBERATELY ITS OWN STATE, NOT PART OF `attributes`: the settings modal
  // writes `attributes` back to the device, and anything parked in there gets
  // re-posted. That is exactly how eff_state was rewound and shift_config was
  // copied between machines (PR #14). This block is READ-ONLY here.
  const [activeManifest, setActiveManifest] = useState(null);

  // Parameter mappings (telemetry key mappings)
  const [paramMappings, setParamMappings] = useState({
    efficiency: 'efficiency',
    hourly_pcs: 'prod_cnt',
    total_pcs: 'total_pcs',
    downtime: 'downtime',
    machine_breakdown: 'machine_breakdown',
    no_material: 'no_material',
    power: 'power',
    quality_issue: 'quality_issue',
    present_operators: 'present_operators',
    // Per-message production delta, written by the rule chain's Calculate Delta
    // node. Summed over an interval this IS that interval's output, so the
    // chart needs no differencing and survives a counter reset.
    hourly_production: 'prod_delta',
    status: 'status',
    idle_count: 'idle_count',
    stopped_count: 'stopped_count'
  });

  // Keys written by the AquaSew rule-chain slot engine. Fixed names (not part of
  // paramMappings) because the engine owns them.
  const SLOT_SIZES = [15, 30, 60];
  const SLOT_KEYS = SLOT_SIZES.reduce((acc, d) => acc.concat([
    `slot${d}_now_eff`, `slot${d}_now_pcs`, `slot${d}_now_target`, `slot${d}_now_state`,
    `slot${d}_window`, `slot${d}_last_eff`, `slot${d}_last_pcs`, `slot${d}_last_window`,
    `slot${d}_last_partial`, `slot${d}_retired`
  ]), []);
  const CONTEXT_KEYS = ['eff_shift', 'eff_shift_window', 'eff_in_break',
    'eff_target_per_hour', 'eff_target_source',
    // minutes per state for the current shift, accumulated by the rule engine
    'state_running_min', 'state_idle_min', 'state_fault_min', 'shift_pcs',
    // shift efficiency and its denominator, written by the rule chain
    'eff_shift_pct', 'eff_shift_target', 'eff_shift_target_full',
    // the device's own state. Listed here so it is fetched and subscribed from
    // the very first request: paramMappings starts as built-in defaults that
    // map status -> "status", a key these devices do not send, and only becomes
    // "runtime_state" once the dashboard config arrives. Until then the status
    // card had nothing to read and showed "no state reported".
    'runtime_state',
    'eff_shift_start_ts', 'eff_shift_state', 'eff_next_start_ts',
    // the manifest identifies the production session
    'manifest_id', 'session_id',
    // pieces for the whole session, accumulated by the rule chain from
    // prod_delta. See the SESSION TOTAL card for why count_total cannot do it.
    'session_pcs'];

  const rawPoint = (key) => {
    const series = telemetryData && telemetryData.data && telemetryData.data[key];
    if (!Array.isArray(series) || series.length === 0) return null;
    return series[series.length - 1];
  };
  // A key is trusted only while it is fresh. ThingsBoard keeps serving the last
  // value of a key that stopped being written (device offline, or a slot size
  // retired from shift_config), which would otherwise look live forever.
  //
  // The threshold is the WRITE CADENCE, not the slot length: the rule-chain
  // engine rewrites every active slot key on every device message (~30 s), so a
  // healthy slot60_window is seconds old even though its slot lasts an hour.
  // Six missed frames is ample evidence the data has stopped.
  const STALE_AFTER_MS = 3 * 60000;
  const rawFresh = (key) => {
    const p = rawPoint(key);
    if (!p) return null;
    if (Date.now() - Number(p.ts) > STALE_AFTER_MS) return null;
    return p.value;
  };
  // A retired size announces itself once. Trust the marker only if it is at
  // least as recent as that size's own keys, so re-enabling a size clears it.
  const slotRetired = (d) => {
    const mark = rawPoint(`slot${d}_retired`);
    if (!mark || String(mark.value) !== '1') return false;
    const win = rawPoint(`slot${d}_window`);
    return !win || Number(mark.ts) >= Number(win.ts);
  };
  const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
  const fmtPct = (v) => (v === null || v === undefined || isNaN(v) ? '—' : `${Math.round(v)}%`);
  // An unset attribute must read as unset, never as a number nobody chose.
  const cfg = (v) => (v === '' || v === null || v === undefined || (typeof v === 'number' && isNaN(v))
    ? 'not set' : v);
  const fmtPcs = (v) => (v === null || v === undefined || isNaN(v) ? '—' : Math.round(v * 10) / 10);
  // ThingsBoard usually hands back a JSON attribute already parsed, but not
  // always -- tolerate both rather than letting a string crash the render.
  const safeJson = (s) => { try { return JSON.parse(s); } catch (e) { return null; } };
  // Joins the parts of a card caption, dropping blanks so a missing field does
  // not leave a dangling separator.
  const caption = (...parts) => parts.filter((p) => p !== null && p !== undefined && p !== '').join(' / ');

  const activeSlotSizes = SLOT_SIZES.filter(
    (d) => !slotRetired(d) && rawFresh(`slot${d}_window`) !== null);
  const slotLabel = (d) => (d === 60 ? 'HOUR' : `${d} MIN`);
  const slotView = (d) => {
    const state = rawFresh(`slot${d}_now_state`);
    return {
      size: d,
      window: rawFresh(`slot${d}_window`),
      state,
      nowEff: state === 'ok' ? num(rawFresh(`slot${d}_now_eff`)) : null,
      nowPcs: num(rawFresh(`slot${d}_now_pcs`)),
      nowTarget: num(rawFresh(`slot${d}_now_target`)),
      lastEff: num(rawFresh(`slot${d}_last_eff`)),
      lastPcs: num(rawFresh(`slot${d}_last_pcs`)),
      lastWindow: rawFresh(`slot${d}_last_window`),
      lastPartial: String(rawFresh(`slot${d}_last_partial`)) === '1'
    };
  };
  // Why a live figure is not a number, in the operator's words
  const nowNote = (state) => {
    if (state === 'break') return 'Break';
    if (state === 'warmup') return 'Starting…';
    if (state === 'notarget') return 'No target set';
    return null;
  };
  const effColour = (v) => (v === null ? '#7a8288' : v >= 90 ? '#2ecc71' : v >= 70 ? '#f39c12' : '#e74c3c');

  // Minutes in each state this shift. Counted from runtime_state by the engine,
  // NOT from runTime/runtime_delta -- that odometer is stuck at 0 on fw
  // v0.1-sewing-wifi, so it would report zero running time on a machine that is
  // demonstrably producing.
  const fmtMins = (v) => {
    if (v === null || v === undefined || isNaN(v)) return null;
    const m = Math.round(Number(v));
    if (m < 60) return `${m}m`;
    return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
  };
  const stateMinutes = {
    running: fmtMins(num(rawFresh('state_running_min'))),
    idle: fmtMins(num(rawFresh('state_idle_min'))),
    fault: fmtMins(num(rawFresh('state_fault_min')))
  };
  // Unformatted, for the STATUS TIME donut. Same three keys the rule chain
  // accumulates per shift; stateMinutes above is their display form.
  const stateMinsRaw = {
    running: num(rawFresh('state_running_min')),
    idle: num(rawFresh('state_idle_min')),
    fault: num(rawFresh('state_fault_min'))
  };

  // Pieces for the whole shift. count_total (TOTAL PCS) is the manifest SESSION
  // counter and resets when a new manifest is loaded, so the two answer
  // different questions and are shown side by side.
  const shiftStartTs = num(rawFresh('eff_shift_start_ts'));
  // history wins; the engine's own tally is the fallback if the query fails
  const shiftPcs = shiftTotal !== null ? shiftTotal : num(rawFresh('shift_pcs'));
  // The engine derives eff_shift_start_ts as (frame ts - whole minutes elapsed),
  // so the seconds of the frame leak into it and the value jitters by up to a
  // minute between frames. Shifts start on a whole minute, so snap to it --
  // otherwise anything keyed on this re-runs on every telemetry frame.
  const shiftStartMin = shiftStartTs ? Math.floor(shiftStartTs / 60000) : null;

  // A session is the lifetime of a manifest: it spans as many shifts and days
  // as needed until a new manifest is pushed. The manifest id is what an
  // operator recognises, so it is shown as the session identity.
  const manifestId = rawFresh('manifest_id');
  const sessionId = rawFresh('session_id');

  const effShiftState = rawFresh('eff_shift_state');
  const effNextStartTs = num(rawFresh('eff_next_start_ts'));
  const outsideShift = effShiftState === 'outside';
  const fmtClock = (ts) => {
    if (!ts) return null;
    const d = new Date(Number(ts));
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const effShift = rawFresh('eff_shift');
  const effShiftWindow = rawFresh('eff_shift_window');
  const effInBreak = rawFresh('eff_in_break');
  const effTargetPerHour = num(rawFresh('eff_target_per_hour'));
  const effTargetSource = rawFresh('eff_target_source');

  // How many hourly columns the shift has, from eff_shift_window ("07:30-17:30"
  // -> 10). A shift crossing midnight (Night 19:30-04:30) has a negative raw
  // length, so add a day. Rounded UP: a 9.5-hour shift ends on a stub hour that
  // still deserves a column.
  const shiftHourCount = (() => {
    const m = /^(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})$/.exec(String(effShiftWindow || ''));
    if (!m) return null;
    let len = (Number(m[3]) * 60 + Number(m[4])) - (Number(m[1]) * 60 + Number(m[2]));
    if (len <= 0) len += 1440;
    return Math.ceil(len / 60);
  })();
  // Clock range for hour i, e.g. i=3 -> "10:30-11:30", for the column tooltip.
  const hourRange = (i) => {
    if (!shiftStartTs) return `H${i + 1}`;
    const from = shiftStartTs + i * 3600000;
    return `${fmtClock(from)}-${fmtClock(from + 3600000)}`;
  };
  // Which column the big number belongs to, so the two are not read as
  // unrelated figures. null outside a shift.
  const currentHourIdx = (shiftStartTs && !outsideShift)
    ? Math.floor((Date.now() - shiftStartTs) / 3600000)
    : null;

  // 60 minutes / SMV = pieces per operator per hour, x planned operators
  const computedTargetPerHour = attributes.smv > 0
    ? Math.round((60 / attributes.smv) * (attributes.plannedOperators || 0))
    : 0;
  const targetMode = attributes.target_mode === 'direct' ? 'direct' : 'smv';
  const effectiveTargetPerHour = targetMode === 'direct'
    ? (Number(attributes.target_per_hour) || 0)
    : computedTargetPerHour;

  const deviceId = selectedDevice?.thingsboard_device_id || dashboard?.userConfig?.thingsboard_device_id;
  const isAdmin = localStorage.getItem('user') && isPlatformAdmin(JSON.parse(localStorage.getItem('user')).role);

  // Blank everything the instant the machine changes, so the previous
  // machine's values are never on screen -- or in a save payload -- while the
  // fetch for the new one is still in flight. Showing one machine's numbers
  // under another machine's name is worse than showing none for a moment.
  useEffect(() => {
    deviceIdRef.current = deviceId;
    setAttributes(DEFAULT_ATTRIBUTES);
    setActiveManifest(null);
    setTelemetryData(null);
    setChartData(null);
    setShiftTotal(null);
    setShiftHours(null);
  }, [deviceId]);

  // Helper function to format date for datetime-local input (local timezone, not UTC)
  const formatDateForInput = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Helper function to parse datetime-local value to Date (local timezone)
  const parseDateFromInput = (value) => {
    if (!value) return null;
    // datetime-local format is YYYY-MM-DDTHH:mm (local time, no timezone)
    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  };

  // Helper function to round date to interval boundary for X-axis labels
  const roundToInterval = (date, interval) => {
    const rounded = new Date(date);
    
    switch (interval) {
      case '15min':
        // Round to nearest 15-minute interval
        const minutes15 = rounded.getMinutes();
        rounded.setMinutes(Math.floor(minutes15 / 15) * 15, 0, 0);
        break;
      case '30min':
        // Round to nearest 30-minute interval
        const minutes30 = rounded.getMinutes();
        rounded.setMinutes(Math.floor(minutes30 / 30) * 30, 0, 0);
        break;
      case 'hourly':
        // Round to the hour
        rounded.setMinutes(0, 0, 0);
        break;
      case 'daily':
      case 'weekly':
        // Round to start of day
        rounded.setHours(0, 0, 0, 0);
        break;
      case 'monthly':
        // Round to start of month
        rounded.setDate(1);
        rounded.setHours(0, 0, 0, 0);
        break;
      default:
        rounded.setMinutes(0, 0, 0);
    }
    
    return rounded;
  };

  // Helper function to calculate default date range based on interval
  const calculateDefaultDates = (interval) => {
    const now = new Date();
    let from = new Date(now);
    let to = new Date(now);

    // Round current time to closest hour
    const roundToHour = (date) => {
      const rounded = new Date(date);
      rounded.setMinutes(0, 0, 0);
      return rounded;
    };

    // Round to closest day (start of day)
    const roundToDay = (date) => {
      const rounded = new Date(date);
      rounded.setHours(0, 0, 0, 0);
      return rounded;
    };

    switch (interval) {
      case '15min': {
        // From: closest of 6 hours back from current time and current date 00:00, round to closest hour
        const sixHoursAgo = new Date(now.getTime() - 6 * 60 * 60 * 1000);
        const todayStart = roundToDay(now);
        from = sixHoursAgo > todayStart ? roundToHour(sixHoursAgo) : todayStart;
        to = now;
        break;
      }
      case '30min': {
        // From: closest of 12 hours back from current time and current date 00:00, round to closest hour
        const twelveHoursAgo = new Date(now.getTime() - 12 * 60 * 60 * 1000);
        const todayStart = roundToDay(now);
        from = twelveHoursAgo > todayStart ? roundToHour(twelveHoursAgo) : todayStart;
        to = now;
        break;
      }
      case 'hourly': {
        // From: closest of 24 hours back from current time and current date 00:00, round to closest hour
        const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const todayStart = roundToDay(now);
        from = twentyFourHoursAgo > todayStart ? roundToHour(twentyFourHoursAgo) : todayStart;
        to = now;
        break;
      }
      case 'daily': {
        // From: The start of the month, round to closest day
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        from = roundToDay(from);
        to = now;
        break;
      }
      case 'weekly': {
        // From: The start of the month, round to closest day
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        from = roundToDay(from);
        to = now;
        break;
      }
      case 'monthly': {
        // From: The start of the year, round to closest day
        from = new Date(now.getFullYear(), 0, 1);
        from = roundToDay(from);
        to = now;
        break;
      }
      default:
        from = roundToHour(new Date(now.getTime() - 24 * 60 * 60 * 1000));
        to = now;
    }

    return { from, to };
  };

  // Initialize default dates on mount and when interval changes
  useEffect(() => {
    const { from, to } = calculateDefaultDates(timeInterval);
    // ThingsBoard marches buckets forward from startTs, so startTs decides
    // where the boundaries fall. Starting at the shift means every bucket is a
    // whole slice of shift; anchored at midnight the first one is a stub -- a
    // 07:30 shift produced a "07:00" bar holding half an hour.
    //
    // Only the intra-shift resolutions do this. One anchor cannot serve a range
    // spanning days: these machines already have 00:00, 08:00 and 07:30 starts
    // in the last week, so a fixed anchor would put older days half an hour out
    // of step with the shift they claim to describe. Those fall back to
    // calendar alignment, which is always well defined.
    let start = from;
    if (INTRA_SHIFT_INTERVALS.indexOf(timeInterval) >= 0 && shiftStartMin) {
      start = new Date(shiftStartMin * 60000);
    }
    console.log('📅 Setting default dates for interval:', {
      interval: timeInterval,
      from: start.toISOString(),
      to: to.toISOString(),
      anchoredTo: start === from ? 'calendar' : 'shift start'
    });
    setFromDate(start);
    setToDate(to);
    setRangeFollowsClock(true);
    // Clear chart data when interval changes to prevent showing stale data
    setChartData(null);
    // Reset the in-flight marker when the interval changes
    chartLoadingRef.current = null;
  }, [timeInterval, shiftStartMin]); // eslint-disable-line react-hooks/exhaustive-deps

  // If dashboard prop is not provided, fetch it ourselves
  useEffect(() => {
    if (!dashboardProp) {
      const dashboardId = window.location.pathname.split('/').pop();
      
      fetch(`/api/dashboards/${dashboardId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
      .then(r => r.json())
      .then(data => {
        setDashboard(data);
      })
      .catch(error => {
        console.error('Failed to fetch dashboard:', error);
      });
    } else {
      setDashboard(dashboardProp);
    }
  }, [dashboardProp]);

  useEffect(() => {
    // Load parameter mappings from dashboard config
    if (dashboard?.config?.paramMappings) {
      console.log('📊 Loading parameter mappings from dashboard config:', dashboard.config.paramMappings);
      setParamMappings(dashboard.config.paramMappings);
    } else {
      console.log('⚠️ No parameter mappings in dashboard config, using defaults');
    }
  }, [dashboard]);

  // Track if initial data load is complete
  const initialLoadCompleteRef = useRef(false);

  useEffect(() => {
    if (deviceId && Object.keys(paramMappings).length > 0) {
      const useWebSocket = dashboard?.userConfig?.use_websocket === true;
      
      if (useWebSocket && !websocketInitializedRef.current) {
        // Initialize WebSocket connection
        initializeWebSocket();
        websocketInitializedRef.current = true;
        initialLoadCompleteRef.current = true;
      } else if (!useWebSocket) {
        // Use polling (original behavior)
        // Wait a bit to ensure paramMappings are loaded, then load data
        const loadInitialData = async () => {
          // Wait for paramMappings to be available
          let retries = 0;
          while (Object.keys(paramMappings).length === 0 && retries < 10) {
            await new Promise(resolve => setTimeout(resolve, 100));
            retries++;
          }
          
          try {
            if (Object.keys(paramMappings).length > 0) {
              await loadData();
              await loadAttributes();
              // shift total is driven by its own effect, in both modes
            } else {
              console.warn('⚠️ Parameter mappings not loaded after waiting');
            }
            initialLoadCompleteRef.current = true;
          } catch (error) {
            console.error('Failed to load initial data:', error);
            initialLoadCompleteRef.current = true; // Still mark as complete to show UI
          }
        };
        
        loadInitialData();
        
        // Auto-refresh every 30 seconds
        pollingIntervalRef.current = setInterval(() => {
          if (Object.keys(paramMappings).length > 0) {
            loadData();
          }
        }, 30000);
      }
      
      return () => {
        // Cleanup
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        // Stop the timers started for THIS device before another selection
        // starts its own, otherwise each machine visited keeps polling forever
        // and they take turns overwriting the chart.
        stopWebSocketTimers();
        if (useWebSocket) {
          thingsBoardWebSocket.disconnect();
          websocketInitializedRef.current = false;
        }
        initialLoadCompleteRef.current = false;
      };
    } else {
      if (!deviceId) {
        setLoading(false);
        setDataError('No machine is assigned to this dashboard for your account.');
      }
    }
  }, [deviceId, paramMappings, dashboard?.userConfig?.use_websocket]);

  // Reload the chart whenever the range or resolution changes. This is an
  // aggregation query the websocket never carries, so it has to be re-fetched
  // in BOTH modes -- it used to be skipped when use_websocket was on, and since
  // changing the resolution also clears chartData, picking 15 or 30 minutes
  // emptied the chart and left it saying "no production data" until the 60s
  // refresh happened to fire.
  useEffect(() => {
    if (deviceId && fromDate && toDate) {
      // Small delay so a burst of state changes settles into one fetch
      const timer = setTimeout(() => {
        loadChartData();
      }, 100);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [fromDate, toDate, timeInterval, deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    if (!deviceId) {
      setDataError('This dashboard has no machine to read data from.');
      setLoading(false);
      return;
    }

    // Check if paramMappings are loaded
    if (Object.keys(paramMappings).length === 0) {
      console.log('⚠️ Parameter mappings not loaded yet, skipping data load');
      return;
    }

    try {
      setRefreshing(true);
      setDataError('');
      
      // Get all unique telemetry keys from mappings (excluding the chart key by KEY name, not value)
      const keys = [...new Set(
        Object.entries(paramMappings)
          .filter(([key, value]) => key !== 'hourly_production')  // Filter by KEY, not value
          .map(([key, value]) => value)
          // slot engine keys ride along in the same request
          .concat(SLOT_KEYS, CONTEXT_KEYS)
      )].join(',');
      
      if (!keys) {
        console.log('⚠️ No telemetry keys to load');
        setRefreshing(false);
        return;
      }
      
      console.log('📡 Loading telemetry with keys:', keys);
      console.log('📊 Current param mappings:', paramMappings);
      
      // Fetch latest telemetry data from ThingsBoard for metrics
      const data = await telemetryService.get(deviceId, keys);
      // The operator may have picked another machine while this was in flight.
      if (deviceId !== deviceIdRef.current) {
        console.log('↩️ Discarding telemetry for a machine no longer selected');
        return;
      }
      console.log('📡 Telemetry data received:', data);
      setTelemetryData({ data });
    } catch (error) {
      console.error('Failed to load telemetry:', error);
      setDataError('Could not load data for this machine. It may be offline, or not sending data yet.');
      // Don't set empty data object, keep previous data if available
      if (!telemetryData) {
        setTelemetryData({ data: {} });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadChartData = async () => {
    if (!deviceId) {
      console.log('⚠️ Cannot load chart data: no deviceId');
      return;
    }

    if (!fromDate || !toDate) {
      console.log('⚠️ Cannot load chart data: dates not set', { fromDate, toDate });
      return;
    }

    // Skip a duplicate poll for the machine already being fetched, but never
    // skip a different machine -- that used to discard the new selection and
    // leave the previous machine's bars on screen until the next refresh.
    if (chartLoadingRef.current === deviceId) {
      console.log('⚠️ Chart data already loading for this machine, skipping duplicate');
      return;
    }

    // Validate dates
    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
      console.error('⚠️ Invalid dates:', { fromDate, toDate });
      return;
    }

    if (fromDate >= toDate) {
      console.error('⚠️ Invalid date range: fromDate >= toDate', { 
        fromDate: fromDate.toISOString(), 
        toDate: toDate.toISOString() 
      });
      return;
    }

    chartLoadingRef.current = deviceId;

    try {
      const chartKey = paramMappings['hourly_production'] || 'prod_delta';
      
      // Convert dates to timestamps
      const startTs = fromDate.getTime();
      const endTs = toDate.getTime();
      
      console.log('📊 Loading chart data:', {
        interval: timeInterval,
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        startTs,
        endTs,
        duration: (endTs - startTs) / (1000 * 60 * 60), // hours
        chartKey
      });
      
      // SUM over per-message deltas gives the interval's output directly. The
      // chart used to take MAX of the cumulative ProductionCount odometer and
      // subtract consecutive buckets, which needed a previous bucket to work --
      // so the very first bar had none and fell back to showing the odometer
      // itself (12167 against a real 213 on 2026-08-11). Differencing also
      // collapsed an hour to zero whenever the counter rewound on reboot.
      const response = await telemetryService.getTimeseries(
        deviceId,
        chartKey,
        timeInterval,
        'SUM',
        startTs,
        endTs
      );
      
      if (deviceId !== deviceIdRef.current) {
        console.log('↩️ Discarding chart data for a machine no longer selected');
        return;
      }
      console.log('📊 Chart data received from ThingsBoard:', response);
      setChartData(response);
    } catch (error) {
      console.error('Failed to load chart data:', error);
      setChartData(null);
    } finally {
      if (chartLoadingRef.current === deviceId) chartLoadingRef.current = null;
    }
  };

  // Sum prod_delta across the whole shift. Hourly buckets are summed here
  // because the backend only forwards named intervals, and it keeps the query
  // small (one bucket per hour of shift).
  const loadShiftTotal = async (startTs) => {
    if (!deviceId || !startTs) return;
    try {
      const res = await telemetryService.getTimeseries(
        deviceId, 'prod_delta', 'hourly', 'SUM', Math.floor(startTs), Date.now());
      const series = (res && res.data && res.data.prod_delta) || (res && res.prod_delta);
      if (!Array.isArray(series)) return;
      const total = series.reduce((sum, p) => sum + (Number(p.value) || 0), 0);
      // The same buckets, kept instead of discarded, so HOURLY PCS can show the
      // shift hour by hour. Indexed off the bucket timestamp rather than array
      // position: ThingsBoard omits empty buckets, so an hour with no production
      // is simply absent and must stay absent (rendered as a dash), not shift
      // every later hour one column to the left.
      const byHour = {};
      series.forEach((p) => {
        const idx = Math.floor((Number(p.ts) - startTs) / 3600000);
        if (idx >= 0) byHour[idx] = (byHour[idx] || 0) + (Number(p.value) || 0);
      });
      if (deviceId !== deviceIdRef.current) return;
      setShiftTotal(total);
      setShiftHours(byHour);
    } catch (e) {
      console.error('Failed to load shift total:', e);
      // leave shiftTotal as-is; the engine tally still renders
    }
  };

  // The shift total is a SUM over history, not a telemetry key, so it cannot
  // stream -- it has to be re-queried as the shift progresses. It used to be
  // called only from the polling branch, so in WebSocket mode it never ran at
  // all and the card silently fell back to the engine's shift_pcs tally, which
  // only moved when someone pressed Refresh. Keyed on the shift start so it
  // also re-runs by itself when the shift rolls over.
  useEffect(() => {
    if (!deviceId || !shiftStartMin) return undefined;
    const startTs = shiftStartMin * 60000;
    loadShiftTotal(startTs);
    const t = setInterval(() => loadShiftTotal(startTs), 60000);
    return () => clearInterval(t);
  }, [deviceId, shiftStartMin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Chrome throttles setInterval in a hidden tab to once a minute and can
  // freeze it outright, so a dashboard left on a second screen or behind
  // another tab can go 20+ minutes without a fetch. Everything on screen is
  // then older than STALE_AFTER_MS and renders as a dash until the next
  // throttled tick -- which is why pressing Refresh appeared to be the only
  // way to bring the numbers back. Catch up the moment the tab is looked at.
  useEffect(() => {
    if (!deviceId) return undefined;
    const onVisibilityChange = () => {
      if (document.hidden) return;
      loadData();
      loadChartData();
      if (shiftStartMin) loadShiftTotal(shiftStartMin * 60000);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [deviceId, shiftStartMin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the chart current without a reload. The window's end used to be
  // stamped once, when the resolution or the shift changed, so a dashboard
  // left up all day stopped gaining bars -- the open bucket kept filling but
  // no new hour ever appeared.
  //
  // Moving toDate re-runs the fetch effect above, so this is the single place
  // the chart refreshes, in polling and websocket modes alike. A pinned range
  // is refreshed in place instead, so its data stays live without the window
  // sliding out from under the operator.
  useEffect(() => {
    if (!deviceId) return undefined;
    const t = setInterval(() => {
      if (rangeFollowsClock) setToDate(new Date());
      else loadChartData();
    }, 60000);
    return () => clearInterval(t);
  }, [deviceId, rangeFollowsClock]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAttributes = async () => {
    if (!deviceId) return;
    
    try {
      const attrs = await deviceService.getAttributes(deviceId, 'SERVER_SCOPE');
      const owned = {};
      (attrs || []).forEach(attr => {
        if (OWNED_ATTRIBUTE_KEYS.includes(attr.key)) owned[attr.key] = attr.value;
      });
      // Replace, never merge. Merging kept attributes from every machine
      // visited in this session, and the save handler then wrote them onto
      // whichever machine was selected.
      setAttributes({ ...DEFAULT_ATTRIBUTES, ...owned });
    } catch (error) {
      console.error('Failed to load attributes:', error);
      // Optionally set an error for attributes specifically
    }

  };

  // Read-only, and never merged into `attributes` -- see the state declaration.
  // A machine with no manifest simply has no such attribute; that is normal,
  // not an error, and the caption is then omitted.
  const loadActiveManifest = async () => {
    if (!deviceId) return;
    try {
      const shared = await deviceService.getAttributes(deviceId, 'SHARED_SCOPE');
      const man = (shared || []).find((a) => a.key === 'active_manifest');
      const val = man && typeof man.value === 'string' ? safeJson(man.value) : man?.value;
      // Late replies must not land on a machine the operator has since left.
      if (deviceIdRef.current === deviceId) setActiveManifest(val || null);
    } catch (error) {
      console.error('Failed to load active manifest:', error);
      if (deviceIdRef.current === deviceId) setActiveManifest(null);
    }
  };

  // Re-read the block whenever the DEVICE reports a different manifest. The id
  // arrives live on telemetry but the style does not, so without this a wall
  // display left up across a manifest change would keep captioning the session
  // with the previous style.
  useEffect(() => {
    loadActiveManifest();
  }, [deviceId, manifestId]); // eslint-disable-line react-hooks/exhaustive-deps

  const initializeWebSocket = async () => {
    try {
      setLoading(true);
      setDataError('');
      
      // Load initial data first
      await loadData();
      await loadChartData();
      await loadAttributes();
      
      // Get all unique telemetry keys from mappings. The slot-engine keys have
      // to ride along exactly as they do in loadData: without them the socket
      // streams only the mapped keys, and the status balls, slot cards and
      // shift context freeze at whatever the initial fetch found -- going blank
      // three minutes later when rawFresh() declares them stale. Before this,
      // pressing Refresh was the only thing that moved them.
      const keys = [...new Set(
        Object.entries(paramMappings)
          .filter(([key, value]) => key !== 'hourly_production')
          .map(([key, value]) => value)
          .concat(SLOT_KEYS, CONTEXT_KEYS)
      )];
      
      // Register callback for WebSocket updates
      const handleTelemetryUpdate = (data) => {
        console.log('📡 WebSocket telemetry update:', data);
        // Update telemetry data in real-time
        setTelemetryData(prev => {
          // Merge new data with existing data
          const updated = { ...prev };
          if (!updated.data) updated.data = {};
          
          Object.keys(data).forEach(key => {
            if (data[key] && Array.isArray(data[key]) && data[key].length > 0) {
              // The two sources disagree on shape. The REST fetch returns
              // {key: [{ts, value}]}, but ThingsBoard's websocket returns
              // {key: [[ts, value]]} -- arrays of pairs. Everything that reads
              // telemetry expects the object form, and a pair silently yields
              // undefined for both .ts and .value: the staleness check then
              // compares against NaN, which is never greater than the
              // threshold, so undefined is served as if it were fresh and every
              // card renders a dash. Normalise before storing.
              const latest = data[key][data[key].length - 1];
              updated.data[key] = Array.isArray(latest)
                ? [{ ts: latest[0], value: latest[1] }]
                : [latest];
            } else if (data[key] && !Array.isArray(data[key])) {
              // Handle single value format
              updated.data[key] = [{ ts: Date.now(), value: data[key] }];
            }
          });
          
          return updated;
        });
      };
      
      // The chart is refreshed by its own effect, which also advances the
      // window; a second timer here would just double the queries.

      telemetryHandlerRef.current = handleTelemetryUpdate;
      thingsBoardWebSocket.onTelemetryUpdate(handleTelemetryUpdate);
      
      // Connect to backend WebSocket proxy (backend handles ThingsBoard authentication)
      await thingsBoardWebSocket.connect(deviceId, keys);
      setWebsocketConnected(true);
      setLoading(false);
      
      // Check connection status periodically
      statusCheckIntervalRef.current = setInterval(() => {
        const isConnected = thingsBoardWebSocket.isConnected();
        setWebsocketConnected(isConnected);
        if (!isConnected) {
          console.warn('⚠️ WebSocket disconnected, attempting to reconnect...');
        }
      }, 5000);
      
      // Teardown is the effect's job -- see stopWebSocketTimers. Returning a
      // cleanup from here is what leaked: this function is called without its
      // result being kept, so the returned closure was never invoked.
      return;
    } catch (error) {
      console.error('Failed to initialize WebSocket:', error);
      setDataError('WebSocket connection failed. Using polling mode (updates every 30 seconds).');
      setWebsocketConnected(false);
      setLoading(false);
      
      // Fallback to polling
      loadData();
      loadChartData();
      loadAttributes();
      pollingIntervalRef.current = setInterval(() => {
        loadData();
        loadChartData();
      }, 30000);
    }
    
    // Listen for WebSocket close events to detect "Unknown session type" errors
    const originalDisconnect = thingsBoardWebSocket.disconnect;
    thingsBoardWebSocket.disconnect = function() {
      originalDisconnect.call(this);
    };
    
    // Check if WebSocket failed due to "Unknown session type"
    wsErrorCheckIntervalRef.current = setInterval(() => {
      if (!thingsBoardWebSocket.isConnected() && websocketInitializedRef.current) {
        // WebSocket failed, ensure polling is active
        if (!pollingIntervalRef.current) {
          console.log('🔄 WebSocket unavailable, ensuring polling is active...');
          pollingIntervalRef.current = setInterval(() => {
            loadData();
            loadChartData();
          }, 30000);
        }
      }
    }, 2000);
  };

  // Everything initializeWebSocket starts, stopped in one place so the effect
  // that owns the device selection can tear it down before starting again.
  const stopWebSocketTimers = () => {
    [chartRefreshIntervalRef, statusCheckIntervalRef, wsErrorCheckIntervalRef].forEach((ref) => {
      if (ref.current) clearInterval(ref.current);
      ref.current = null;
    });
    if (telemetryHandlerRef.current) {
      thingsBoardWebSocket.offTelemetryUpdate(telemetryHandlerRef.current);
      telemetryHandlerRef.current = null;
    }
  };

  const handleUpdateAttributes = async (e) => {
    e.preventDefault();
    
    if (!deviceId) {
      alert('Cannot save: this dashboard has no machine assigned.');
      return;
    }

    try {
      // Send only the keys this form owns. Spreading the whole attribute object
      // also posted eff_state back from a page-load snapshot, rewinding the
      // rule chain's running/idle/fault minutes to whatever they were when the
      // page opened.
      // target_per_hour is always written, even in SMV mode, so the stored
      // value documents the target the rule chain will actually use.
      const payload = {
        target_mode: targetMode,
        target_per_hour: effectiveTargetPerHour
      };
      // Blank means "nobody has set this", so do not write it. Otherwise the
      // empty defaults would simply replace the old invented numbers with empty
      // attributes -- still the form inventing device state rather than
      // recording what an operator actually entered.
      OWNED_ATTRIBUTE_KEYS.forEach(key => {
        const v = attributes[key];
        if (payload[key] !== undefined) return;
        if (v === '' || v === null || v === undefined) return;
        if (typeof v === 'number' && isNaN(v)) return;
        payload[key] = v;
      });
      await deviceService.updateAttributes(deviceId, 'SERVER_SCOPE', payload);
      alert('Server attributes updated successfully!');
      setShowSettings(false);
    } catch (error) {
      alert('Failed to update attributes: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSaveParamMappings = async (e) => {
    e.preventDefault();
    
    if (!dashboard) {
      alert('Dashboard not loaded. Please wait for the dashboard to load before configuring parameters.');
      return;
    }
    
    // Get dashboard_id from URL if not in dashboard object
    const dashboardId = dashboard.dashboard_id || window.location.pathname.split('/').pop();
    
    if (!dashboardId || dashboardId === 'dashboard') {
      alert('Unable to determine dashboard ID. Please refresh the page and try again.');
      return;
    }
    
    try {
      // Save parameter mappings to dashboard config
      const response = await fetch(`/api/dashboards/${dashboardId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          config: {
            ...(dashboard.config || {}),
            paramMappings: paramMappings
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save parameter mappings');
      }

      alert('Parameter mappings saved successfully! Dashboard will reload with new mappings.');
      setShowParamMapping(false);
      
      // Reload the page to get updated dashboard config
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error('Failed to save parameter mappings:', error);
      alert('Failed to save parameter mappings: ' + error.message);
    }
  };

  // Parse telemetry data for display using mapped keys
  const getLatestValue = (mappingKey, defaultValue = 0) => {
    const telemetryKey = paramMappings[mappingKey] || mappingKey;
    
    // If data hasn't loaded yet, return null to show loading state
    if (!telemetryData) {
      return null;
    }
    
    if (!telemetryData.data || !telemetryData.data[telemetryKey]) {
      // If data structure exists but key is missing, return default value
      // This means data was loaded but this specific key doesn't exist
      return defaultValue;
    }
    
    const values = telemetryData.data[telemetryKey];
    if (!Array.isArray(values) || values.length === 0) return defaultValue;
    
    const latestValue = values[values.length - 1]?.value;
    // Handle null/undefined values
    if (latestValue === null || latestValue === undefined) return defaultValue;
    
    // For string values (like status), return as-is; for numbers, parse
    if (typeof defaultValue === 'string') {
      return String(latestValue);
    }
    
    return latestValue;
  };

  // The efficiency meters show the SHIFT, not the current hour. eff_shift_pct
  // is written by the rule chain from the same formula the slot cards use --
  // pieces against the target for the productive time elapsed, breaks removed --
  // so there is one definition of efficiency rather than two. The legacy
  // `efficiency` key mirrors the current HOUR and is still shown, unchanged, on
  // the HOUR NOW slot card.
  const efficiency = num(rawFresh('eff_shift_pct'));
  const shiftEffTarget = num(rawFresh('eff_shift_target'));
  const hourlyPcs = getLatestValue('hourly_pcs', 0);
  // Same figure, but freshness-gated, for the strip's in-progress cell. The
  // strip re-queries hourly SUM buckets once a minute while this streams on
  // every frame, so the two disagreed by a few pieces on the current hour --
  // the cell was simply up to 60 s behind the number under it. Completed hours
  // always matched. Gated because getLatestValue is not: on an offline machine
  // a stale value would otherwise overwrite a bucket that is actually correct.
  const liveHourPcs = num(rawFresh(paramMappings.hourly_pcs || 'prod_hour'));
  // SESSION TOTAL comes from the rule chain's session accumulator, never from
  // count_total. count_total is a firmware odometer that zeroes on every power
  // cycle, and operators switch these machines off whenever they are not in
  // use -- so it reported "pieces since the last power-on", not the session.
  // Measured 2026-09-10 on SUPREME 5: the card read 1,692 for a session whose
  // real output was 51,921. session_pcs is summed from prod_delta and so has
  // nothing to reset.
  //
  // Read UNGATED by freshness, unlike the slot cards: a session total is a
  // cumulative fact, not a live reading. It stays true while the machine is
  // switched off, which is exactly when an operator wants to see it. If the
  // key has never been written the card shows nothing rather than falling back
  // to count_total, which would just be the old wrong number again.
  const sessionPoint = rawPoint('session_pcs');
  const totalPcs = sessionPoint ? num(sessionPoint.value) : null;
  const downtime = getLatestValue('downtime', 0);
  const machineBreakdown = getLatestValue('machine_breakdown', 0);
  const noMaterial = getLatestValue('no_material', 0);
  const power = getLatestValue('power', 0);
  const qualityIssue = getLatestValue('quality_issue', 0);
  const presentOperators = getLatestValue('present_operators', 0);
  
  // Line status parameters
  // Prefer the device's own key, which is always fetched (see CONTEXT_KEYS), so
  // the machine's state is right on the first paint rather than after the
  // saved parameter mappings arrive. The mapped key remains the fallback for
  // deployments that report state under a different name.
  // Both reads go through rawFresh so the staleness rule applies either way --
  // getLatestValue has no freshness check, so falling back through it would
  // present a state from a machine that stopped reporting ten minutes ago as
  // though it were live.
  const mappedStatusKey = paramMappings.status || 'status';
  const statusValue = rawFresh('runtime_state') ?? rawFresh(mappedStatusKey) ?? '';

  // Prepare production data from ThingsBoard aggregated timeseries
  const prepareProductionData = () => {
    if (!chartData?.data) {
      return [];
    }

    // Where the bucket grid starts. ThingsBoard was given this as startTs, so
    // every bucket boundary is a whole number of intervals from it.
    const anchorTs = fromDate ? fromDate.getTime() : null;

    const chartKey = paramMappings['hourly_production'] || 'prod_cnt';
    const values = chartData.data[chartKey];

    if (!values || !Array.isArray(values) || values.length === 0) {
      return [];
    }

    // Sort values by timestamp to ensure correct order
    const sortedValues = [...values]
      .filter(item => item && item.ts && !isNaN(item.ts)) // Filter out invalid entries
      .sort((a, b) => a.ts - b.ts);
    
    if (sortedValues.length === 0) {
      console.log('⚠️ No valid chart values after filtering');
      return [];
    }
    
    console.log('📊 Processing chart values:', {
      total: sortedValues.length,
      first: sortedValues[0] ? { ts: new Date(sortedValues[0].ts).toISOString(), value: sortedValues[0].value } : null,
      last: sortedValues[sortedValues.length - 1] ? { ts: new Date(sortedValues[sortedValues.length - 1].ts).toISOString(), value: sortedValues[sortedValues.length - 1].value } : null,
      interval: timeInterval
    });

    // Calculate production for each interval by taking the difference
    // between consecutive cumulative values
    const data = sortedValues.map((item) => {
      // ThingsBoard stamps each bucket at its MIDPOINT, so a 07:30-08:30 bucket
      // arrives stamped 08:00. Flooring that to the hour would label it 08:00.
      // Walk back to the bucket's start from the anchor instead, which is also
      // correct for the final partial bucket (whose midpoint is not the same
      // distance from its start as a full one).
      const intervalMs = INTERVAL_MS[timeInterval] || INTERVAL_MS.hourly;
      const bucketStart = anchorTs === null
        ? item.ts
        : anchorTs + Math.floor((item.ts - anchorTs) / intervalMs) * intervalMs;
      const roundedDate = new Date(bucketStart);
      let label = '';

      switch (timeInterval) {
        case '15min':
          // Show time rounded to 15-minute intervals (00:00, 00:15, 00:30, 00:45)
          label = roundedDate.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
          });
          break;
        case '30min':
          // Show time rounded to 30-minute intervals (00:00, 00:30, 01:00, 01:30)
          label = roundedDate.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
          });
          break;
        case 'hourly':
          // Show time rounded to hour (00:00, 01:00, 02:00, etc.)
          label = roundedDate.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
          });
          break;

        case 'daily':
        case 'weekly':
          // Date format for daily and weekly intervals
          label = roundedDate.toLocaleDateString('en-GB', { 
            day: '2-digit', 
            month: 'short' 
          });
          break;

        case 'monthly':
          // Month format for monthly intervals
          label = roundedDate.toLocaleDateString('en-US', { 
            month: 'short', 
            year: 'numeric' 
          });
          break;

        default:
          label = roundedDate.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
          });
      }

      // The bucket already holds the interval's output -- SUM of the per-message
      // deltas over that interval -- so it is used as-is. No differencing
      // against the previous bucket, which is what made the first bar
      // meaningless, and no guessing whether the series looks cumulative.
      const pcs = parseFloat(item.value) || 0;

      return {
        time: label,
        pcs: Math.round(pcs),
        isHighlighted: false,
        timestamp: roundedDate.getTime() // Store timestamp for sorting
      };
    });

    // Sort by timestamp to ensure correct order (in case rounding caused issues)
    const sortedData = data.sort((a, b) => a.timestamp - b.timestamp);
    
    // Remove timestamp from final data (not needed for chart display)
    const finalData = sortedData.map(({ timestamp, ...rest }) => rest);
    
    return finalData;
  };

  const productionData = prepareProductionData();
  // Total across whatever range is on screen, shown under the axis so the bars
  // add up to something visible rather than having to be eyeballed.
  const rangeTotal = productionData.reduce((sum, d) => sum + (Number(d.pcs) || 0), 0);
  // True when the buckets follow the shift rather than the calendar -- worth
  // saying out loud, otherwise labels silently moving from 07:00 to 07:30 reads
  // as a bug.
  const chartAnchoredToShift = INTRA_SHIFT_INTERVALS.indexOf(timeInterval) >= 0
    && !!shiftStartMin && !!fromDate && fromDate.getTime() === shiftStartMin * 60000;

  // Get chart title based on interval
  const getChartTitle = () => {
    switch (timeInterval) {
      case '15min': return '15-MINUTE PRODUCTION';
      case '30min': return '30-MINUTE PRODUCTION';
      case 'hourly': return 'HOURLY PRODUCTION';
      case 'daily': return 'DAILY PRODUCTION';
      case 'weekly': return 'WEEKLY PRODUCTION';
      case 'monthly': return 'MONTHLY PRODUCTION';
      default: return 'PRODUCTION';
    }
  };

  // Colour for the efficiency bar (the gauge it was named for is gone)
  const getEfficiencyColor = () => {
    if (efficiency >= 80) return '#2ecc71';
    if (efficiency >= 60) return '#f39c12';
    return '#e74c3c';
  };

  // Line status - use status parameter if available, otherwise fall back to efficiency-based calculation
  // The device reports runtime_state directly (PrimeFlow vocabulary:
  // running | idle | fault). Observed across the fleet over 30 days: only
  // 'running' and 'idle' have ever occurred; 'fault' is real but has not fired.
  // Anything else -> unknown. We deliberately do NOT infer a state from the
  // efficiency figure any more: a guessed state is indistinguishable from a
  // measured one on screen, which is how stale/invented values mislead.
  const LINE_STATES = [
    { key: 'running', label: 'RUNNING', colour: '#2ecc71' },
    { key: 'idle', label: 'IDLE', colour: '#f39c12' },
    { key: 'fault', label: 'FAULT', colour: '#e74c3c' }
  ];
  // ---- STATUS TIME donut -------------------------------------------------
  // Share of the shift spent in each machine state. Reuses LINE_STATES so the
  // colours are the same language as the MACHINE STATUS card beside it --
  // "running" must not be green in one card and blue in the next.
  // FAULT is only drawn when it actually happened: the fleet sits at 0 fault
  // minutes, and a permanent 0% slice in the legend trains people to ignore it.
  const DONUT_R = 62;
  const DONUT_C = 2 * Math.PI * DONUT_R;
  const DONUT_GAP = 3;            // surface gap so adjacent fills never touch
  const statusSegments = (() => {
    const segs = LINE_STATES
      .map((st) => ({ ...st, mins: stateMinsRaw[st.key] }))
      .filter((st) => st.mins !== null && st.mins > 0);
    const total = segs.reduce((t, st) => t + st.mins, 0);
    if (!total) return { total: 0, parts: [] };
    let acc = 0;
    // The gap exists to stop adjacent fills touching, so a lone segment gets
    // none -- otherwise a machine that ran all shift shows a ring with an
    // unexplained notch cut out of it at 12 o'clock.
    const gap = segs.length > 1 ? DONUT_GAP : 0;
    const parts = segs.map((st) => {
      const frac = st.mins / total;
      const len = Math.max(0, frac * DONUT_C - gap);
      // mid-angle of the arc, measured from 12 o'clock
      const mid = (acc + frac / 2) * 2 * Math.PI - Math.PI / 2;
      const part = {
        ...st,
        frac,
        dash: `${len} ${DONUT_C - len}`,
        offset: -(acc * DONUT_C + gap / 2),
        lx: 100 + DONUT_R * Math.cos(mid),
        ly: 100 + DONUT_R * Math.sin(mid)
      };
      acc += frac;
      return part;
    });
    return { total, parts };
  })();
  const runningShare = statusSegments.total
    ? (stateMinsRaw.running || 0) / statusSegments.total * 100
    : null;

  const rawState = String(statusValue || '').toLowerCase();
  // tolerate legacy/simulator spellings so a machine is never shown as unknown
  const stateAliases = { run: 'running', stopped: 'fault', stop: 'fault', error: 'fault' };
  const lineStatus = LINE_STATES.some((s) => s.key === rawState)
    ? rawState
    : (stateAliases[rawState] || null);

  if (loading) {
    return (
      <div className="production-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="production-dashboard">
      {/* Header */}
      <div className="dashboard-title">
        <div className="title-content">
          <h1>{attributes.task || 'NO STYLE SET'}</h1>
          <div className="subtitle">
            <span>Date: {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            <span>|</span>
            <span>Time: {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
            <span>|</span>
            <span>Target Efficiency: {attributes.targetEfficiency === '' || attributes.targetEfficiency === null || attributes.targetEfficiency === undefined ? 'not set' : `${attributes.targetEfficiency}%`}</span>
            {/* Shift context from the rule-chain slot engine. Read-only here —
                engineers set shifts and intervals in the ThingsBoard widget. */}
            {selectedDevice && (
              <>
                <span>|</span>
                <span>Machine: {selectedDevice.device_name}</span>
              </>
            )}
            {effShift && !outsideShift && (
              <>
                <span>|</span>
                <span>Shift: {effShift}{effShiftWindow ? ` (${effShiftWindow})` : ''}</span>
              </>
            )}
            {outsideShift && (
              <>
                <span>|</span>
                <span className="shift-ended">
                  Outside shift{effShift ? ` — next: ${effShift}` : ''}
                  {effNextStartTs ? ` at ${fmtClock(effNextStartTs)}` : ''}
                </span>
              </>
            )}
            {effTargetPerHour !== null && !outsideShift && (
              <>
                <span>|</span>
                <span>Target: {fmtPcs(effTargetPerHour)}/h{effTargetSource ? ` (${effTargetSource})` : ''}</span>
              </>
            )}
            <span>|</span>
            {manifestId || sessionId ? (
              <span title={sessionId ? `session ${sessionId}` : ''}>
                Session: {manifestId || sessionId}
              </span>
            ) : (
              <span className="shift-missing">Session: none (no manifest loaded)</span>
            )}
            {effInBreak ? (
              <>
                <span>|</span>
                <span className="break-flag">On interval: {effInBreak}</span>
              </>
            ) : null}
            {!effShift && !outsideShift && (
              <>
                <span>|</span>
                <span className="shift-missing">No shift data — device offline, or shift_config not set</span>
              </>
            )}
            {dashboard?.userConfig?.use_websocket && (
              <span className="websocket-status">
                {websocketConnected ? (
                  <>
                    <Wifi size={14} style={{ color: '#2ecc71', marginLeft: '10px' }} />
                    <span style={{ color: '#2ecc71', marginLeft: '5px' }}>Live</span>
                  </>
                ) : (
                  <>
                    <WifiOff size={14} style={{ color: '#e74c3c', marginLeft: '10px' }} />
                    <span style={{ color: '#e74c3c', marginLeft: '5px' }}>Connecting...</span>
                  </>
                )}
              </span>
            )}
          </div>
        </div>
            <div className="header-actions">
              <button className="btn-icon" onClick={loadData} disabled={refreshing} title="Refresh">
                <RefreshCw size={20} className={refreshing ? 'spinning' : ''} />
              </button>
              {isAdmin && (
                <button 
                  className="btn-icon" 
                  onClick={() => {
                    if (!dashboard) {
                      alert('Please wait for dashboard to finish loading');
                      return;
                    }
                    setShowParamMapping(true);
                  }} 
                  disabled={!dashboard}
                  title="Configure Parameters"
                >
                  <Sliders size={20} />
                </button>
              )}
              <button className="btn-icon" onClick={() => setShowSettings(true)} title="Settings">
                <Settings size={20} />
              </button>
            </div>
      </div>

      {/* Main Grid */}
      {/* Slot efficiency: per configured slot size, the closed slot (final) and
          the slot in progress (pace). Keys come from the rule-chain engine. */}
      {activeSlotSizes.length > 0 && (
        <div className="slot-row">
          {activeSlotSizes.map((d) => {
            const v = slotView(d);
            const note = nowNote(v.state);
            return (
              <React.Fragment key={d}>
                <div className="slot-card">
                  <div className="slot-card-head">
                    <span className="slot-size">{slotLabel(d)}</span>
                    <span className="slot-kind">LAST</span>
                  </div>
                  <div className="slot-value" style={{ color: effColour(v.lastEff) }}>
                    {fmtPct(v.lastEff)}
                    {v.lastPartial && <span className="slot-partial" title="Closed early because the shift config changed">partial</span>}
                  </div>
                  <div className="slot-sub">
                    {v.lastWindow || '—'}{v.lastPcs !== null ? ` · ${fmtPcs(v.lastPcs)} pcs` : ''}
                  </div>
                </div>
                <div className="slot-card slot-card-now">
                  <div className="slot-card-head">
                    <span className="slot-size">{slotLabel(d)}</span>
                    <span className="slot-kind">NOW</span>
                  </div>
                  <div className="slot-value" style={{ color: note ? '#7a8288' : effColour(v.nowEff) }}>
                    {note || fmtPct(v.nowEff)}
                  </div>
                  <div className="slot-sub">
                    {v.window || '—'}
                    {v.nowPcs !== null ? ` · ${fmtPcs(v.nowPcs)}` : ''}
                    {v.nowTarget ? ` / ${fmtPcs(v.nowTarget)} pcs` : ''}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      <div className="dashboard-grid">
        {/* Line Status Card */}
        <div className="metric-card line-status-card-grid">
          <div className="card-header">MACHINE STATUS</div>
          <div className="status-indicators-compact">
            {LINE_STATES.map((st) => {
              const on = lineStatus === st.key;
              // time spent in this state during the current shift
              const mins = stateMinutes[st.key];
              return (
                <div
                  key={st.key}
                  className={`status-indicator-compact ${on ? 'active' : ''}`}
                  style={{ background: on ? st.colour : '#3a3a3a' }}
                  title={on ? `Machine is ${st.label.toLowerCase()}` : ''}
                >
                  <span className="status-label-compact">{st.label}</span>
                  {mins ? <div className="status-count-compact">{mins}</div> : null}
                </div>
              );
            })}
            {!lineStatus && <span className="status-unknown">no state reported</span>}
          </div>
        </div>

        {/* Efficiency Card */}
        <div className="metric-card efficiency-card">
          <div className="card-header">SHIFT EFFICIENCY — CUMULATIVE</div>
          <div className="efficiency-target">Target: {attributes.targetEfficiency === '' || attributes.targetEfficiency === null || attributes.targetEfficiency === undefined ? 'not set' : `${attributes.targetEfficiency}%`}</div>
          <div className="efficiency-value">{fmtPct(num(efficiency))}</div>
          {shiftPcs !== null && shiftEffTarget !== null && (
            <div className="efficiency-progress">
              {fmtPcs(shiftPcs)} / {fmtPcs(shiftEffTarget)} pcs
            </div>
          )}
          <div className="efficiency-bar">
            <div 
              className="efficiency-fill"
              style={{ 
                width: `${Math.min(100, Math.max(0, Number(efficiency) || 0))}%`,
                background: `linear-gradient(90deg, ${getEfficiencyColor()} 0%, ${getEfficiencyColor()}dd 100%)`
              }}
            />
          </div>
        </div>

        {/* Hourly PCS: the big number is the CURRENT hour; the strip above it is
            every hour of the shift, so a whole shift reads at a glance. Both
            come from prod_delta -- COMPLETED hours from hourly SUM buckets over
            history, the hour still running from the rule chain's live slot, so
            the in-progress cell and the number below it are the same value. */}
        <div className="metric-card">
          <div className="card-header">HOURLY PCS</div>
          {shiftHourCount ? (
            <div className="hour-strip" role="table" aria-label="Pieces per hour of shift">
              {Array.from({ length: shiftHourCount }, (_, i) => {
                const bucket = shiftHours ? shiftHours[i] : undefined;
                const isNow = currentHourIdx === i;
                // the hour still running comes from the live slot, so this cell
                // and the big number below can never disagree
                const v = (isNow && liveHourPcs !== null) ? liveHourPcs : bucket;
                return (
                  <div
                    key={i}
                    className={`hour-cell${isNow ? ' now' : ''}`}
                    title={hourRange(i)}
                  >
                    <div className="hour-label">H{i + 1}</div>
                    {/* An hour with no bucket has not run, or produced nothing --
                        either way a dash is honest and 0 would not be. */}
                    <div className="hour-value">{v === undefined || v === null ? '—' : fmtPcs(v)}</div>
                  </div>
                );
              })}
            </div>
          ) : null}
          <div className="metric-value" style={{ color: '#e74c3c' }}>{fmtPcs(num(hourlyPcs))}</div>
        </div>

        {/* Session total: session_pcs, accumulated by the rule chain from
            prod_delta. Resets when a new manifest is loaded, NOT at shift
            change and NOT when the machine is switched off. */}
        <div className="metric-card">
          <div className="card-header">
            <span className="card-header-title">SESSION TOTAL</span>
            {/* manifest_id / style_id / style_name. manifest_id is taken from
                telemetry when present -- that is what the DEVICE is actually
                running -- and falls back to the stored block for a machine
                that has not fetched it yet. */}
            {(() => {
              const note = caption(
                manifestId || activeManifest?.manifest_id,
                activeManifest?.style_id,
                activeManifest?.style_name);
              return note ? <span className="card-header-note" title={note}>{note}</span> : null;
            })()}
          </div>
          <div className="metric-value" style={{ color: '#4a90e2' }}>{fmtPcs(num(totalPcs))}</div>
        </div>

        {/* Shift total: distinct from TOTAL PCS, which resets per manifest */}
        {shiftPcs !== null && (
          <div className="metric-card">
            <div className="card-header">
              <span className="card-header-title">SHIFT TOTAL</span>
              {/* e.g. "General 07:30-17:30". Both keys come from the rule
                  chain's slot engine and stop being written outside a shift,
                  so rawFresh already withholds a stale window. */}
              {(() => {
                const note = [effShift, effShiftWindow].filter(Boolean).join(' ');
                return note ? <span className="card-header-note" title={note}>{note}</span> : null;
              })()}
            </div>
            <div className="metric-value" style={{ color: '#9b59b6' }}>{fmtPcs(shiftPcs)}</div>
          </div>
        )}

        {/* Information */}
        <div className="metric-card style-info-card">
          <div className="card-header" style={{ background: '#4a90e2' }}>INFORMATION</div>
          <div className="style-info-content">
            <div className="info-row">
              <span className="info-label">Task:</span>
              <span className="info-value">{attributes.task || 'NO STYLE SET'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">SMV:</span>
              <span className="info-value">{cfg(attributes.smv)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Operations:</span>
              <span className="info-value">{cfg(attributes.operations)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Planned Operators:</span>
              <span className="info-value">{cfg(attributes.plannedOperators)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Present Operators:</span>
              <span className="info-value">{fmtPcs(num(presentOperators))}</span>
            </div>
          </div>
        </div>

        {/* Production Chart */}
        <div className="chart-card hourly-production-card">
          <div className="card-header">
            <span>{getChartTitle()}</span>
            <div className="chart-controls">
              <div className="date-range-controls">
                <label className="date-label">From:</label>
                <input
                  type="datetime-local"
                  value={formatDateForInput(fromDate)}
                  onChange={(e) => {
                    const newDate = parseDateFromInput(e.target.value);
                    setFromDate(newDate);
                    setRangeFollowsClock(false);
                  }}
                  className="date-input"
                />
                <label className="date-label">To:</label>
                <input
                  type="datetime-local"
                  value={formatDateForInput(toDate)}
                  onChange={(e) => {
                    const newDate = parseDateFromInput(e.target.value);
                    setToDate(newDate);
                    setRangeFollowsClock(false);
                  }}
                  className="date-input"
                />
              </div>
              <select 
                value={timeInterval} 
                onChange={(e) => {
                  setTimeInterval(e.target.value);
                  // Dates will be updated by useEffect
                }}
                className="interval-selector"
              >
                <option value="15min">15 Minutes</option>
                <option value="30min">30 Minutes</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
          <div className="chart-container">
            {productionData.length === 0 ? (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%', 
                color: '#888',
                fontSize: '14px'
              }}>
                No production data available for selected interval
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={productionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3a" />
                  <XAxis 
                    dataKey="time" 
                    stroke="#a0a0a0"
                    angle={timeInterval === 'daily' || timeInterval === 'weekly' || timeInterval === 'monthly' ? -45 : 0}
                    textAnchor={timeInterval === 'daily' || timeInterval === 'weekly' || timeInterval === 'monthly' ? 'end' : 'middle'}
                    height={timeInterval === 'daily' || timeInterval === 'weekly' || timeInterval === 'monthly' ? 80 : 60}
                  />
                  <YAxis stroke="#a0a0a0" />
                  <Tooltip
                    contentStyle={{
                      background: '#2a2a2a',
                      border: '1px solid #3a3a3a',
                      borderRadius: '5px'
                    }}
                    labelStyle={{ color: '#ffffff', fontWeight: 600 }}
                    itemStyle={{ color: '#ffffff' }}
                    cursor={{ fill: 'rgba(255,255,255,0.06)' }}
                    formatter={(value) => [`${Math.round(value)} pcs`, 'Production']}
                  />
                  <Bar dataKey="pcs" radius={[4, 4, 0, 0]}>
                    {productionData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.isHighlighted ? '#e74c3c' : '#2ecc71'} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          {productionData.length > 0 && (
            <div className="chart-total">
              <span className="chart-total-label">
                Total{chartAnchoredToShift ? ' this shift' : ' for selected range'}
              </span>
              <span className="chart-total-value">{fmtPcs(rangeTotal)} pcs</span>
              <span className="chart-total-note">
                {chartAnchoredToShift
                  ? 'hours aligned to shift start'
                  : 'calendar-aligned buckets'}
              </span>
            </div>
          )}
        </div>

        {/* Downtime */}
        <div className="metric-card downtime-card">
          <div className="card-header">DOWNTIME (MIN)</div>
          <div className="metric-value" style={{ color: '#000' }}>{fmtPcs(num(downtime))}</div>
        </div>

        {/* Status time: how the shift has been spent, accumulated by the rule
            chain. Sits beside MACHINE STATUS, which shows the state RIGHT NOW --
            this is the same three states integrated over the shift. Replaced the
            efficiency speedometer, which duplicated the SHIFT EFFICIENCY card. */}
        <div className="chart-card status-time-card">
          <div className="card-header">STATUS TIME — CUMULATIVE</div>
          {statusSegments.total ? (
            <div className="donut-wrap">
              <svg viewBox="0 0 200 200" className="donut-svg" role="img"
                   aria-label="Share of the shift spent in each machine state">
                {/* track, so the ring still reads as a ring while thin */}
                <circle cx="100" cy="100" r={DONUT_R} fill="none"
                        stroke="var(--border-color)" strokeWidth="26" />
                {statusSegments.parts.map((p) => (
                  <circle
                    key={p.key}
                    cx="100" cy="100" r={DONUT_R}
                    fill="none"
                    stroke={p.colour}
                    strokeWidth="26"
                    strokeDasharray={p.dash}
                    strokeDashoffset={p.offset}
                    transform="rotate(-90 100 100)"
                  >
                    <title>{`${p.label}: ${fmtMins(p.mins)} (${Math.round(p.frac * 100)}%)`}</title>
                  </circle>
                ))}
                {/* Percentages sit ON the fill, so they wear an on-colour ink
                    rather than the page's text token -- white on #2ecc71 or
                    #f39c12 is unreadable. Hidden under 8%: the arc is too short
                    to hold the text without it colliding with its neighbour. */}
                {statusSegments.parts.filter((p) => p.frac >= 0.08).map((p) => (
                  <text key={p.key} x={p.lx} y={p.ly} className="donut-slice-label"
                        textAnchor="middle" dominantBaseline="central">
                    {Math.round(p.frac * 100)}%
                  </text>
                ))}
              </svg>
              <div className="donut-centre">
                <div className="donut-centre-value">{fmtPct(runningShare)}</div>
                <div className="donut-centre-caption">RUNNING</div>
              </div>
            </div>
          ) : (
            <div className="donut-empty">No status time yet this shift</div>
          )}
          {statusSegments.total ? (
            <div className="donut-legend">
              {statusSegments.parts.map((p) => (
                <div key={p.key} className="donut-legend-item">
                  <span className="donut-dot" style={{ background: p.colour }} />
                  <span className="donut-legend-label">{p.label}</span>
                  <span className="donut-legend-value">{fmtMins(p.mins)}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* Downtime Reasons */}
        <div className="chart-card downtime-reasons-card">
          <div className="card-header">DOWNTIME REASONS</div>
          <div className="downtime-list">
            <div className="downtime-item">
              <div className="downtime-label">
                <span className="downtime-icon" style={{ background: '#e74c3c' }}></span>
                Machine Breakdown
              </div>
              <div className="downtime-value">{fmtPcs(num(machineBreakdown))}</div>
              <div className="downtime-bar-icon" style={{ background: '#e74c3c' }}></div>
            </div>
            <div className="downtime-item">
              <div className="downtime-label">
                <span className="downtime-icon" style={{ background: '#f39c12' }}></span>
                No Material
              </div>
              <div className="downtime-value">{fmtPcs(num(noMaterial))}</div>
              <div className="downtime-bar-icon" style={{ background: '#f39c12' }}></div>
            </div>
            <div className="downtime-item">
              <div className="downtime-label">
                <span className="downtime-icon" style={{ background: '#2ecc71' }}></span>
                Power
              </div>
              <div className="downtime-value">{fmtPcs(num(power))}</div>
              <div className="downtime-bar-icon" style={{ background: '#2ecc71' }}></div>
            </div>
            <div className="downtime-item">
              <div className="downtime-label">
                <span className="downtime-icon" style={{ background: '#f39c12' }}></span>
                Quality Issue
              </div>
              <div className="downtime-value">{fmtPcs(num(qualityIssue))}</div>
              <div className="downtime-bar-icon" style={{ background: '#f39c12' }}></div>
            </div>
          </div>
        </div>

      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Update Server Attributes</h2>
              <button className="modal-close" onClick={() => setShowSettings(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleUpdateAttributes} className="modal-body">
              <div className="form-group">
                <label>Task / Dashboard Title</label>
                <input
                  type="text"
                  value={attributes.task}
                  onChange={(e) => setAttributes({ ...attributes, task: e.target.value })}
                  placeholder="e.g., T-SHIRT MODEL X"
                />
              </div>
              <div className="form-group">
                <label>Target Efficiency (%)</label>
                <input
                  type="number"
                  value={attributes.targetEfficiency ?? ''}
                  onChange={(e) => setAttributes({ ...attributes, targetEfficiency: parseFloat(e.target.value) })}
                  min="0"
                  max="100"
                  step="1"
                />
              </div>
              {/* How the hourly target is defined. Some sites know SMV and
                  operator count; others only know pieces/hour. Whichever mode
                  is chosen, the other side's fields are disabled. */}
              <div className="form-group target-mode-group">
                <label>How is the hourly target set?</label>
                <div className="target-mode-options">
                  <label className="target-mode-option">
                    <input
                      type="radio"
                      name="target_mode"
                      value="smv"
                      checked={targetMode === 'smv'}
                      onChange={() => setAttributes({ ...attributes, target_mode: 'smv' })}
                    />
                    <span>From SMV &amp; operators</span>
                  </label>
                  <label className="target-mode-option">
                    <input
                      type="radio"
                      name="target_mode"
                      value="direct"
                      checked={targetMode === 'direct'}
                      onChange={() => setAttributes({ ...attributes, target_mode: 'direct' })}
                    />
                    <span>Direct target per hour</span>
                  </label>
                </div>
              </div>

              <div className={`form-group${targetMode !== 'smv' ? ' field-disabled' : ''}`}>
                <label>Planned Operators</label>
                <input
                  type="number"
                  value={attributes.plannedOperators ?? ''}
                  onChange={(e) => setAttributes({ ...attributes, plannedOperators: parseInt(e.target.value) })}
                  min="1"
                  step="1"
                  disabled={targetMode !== 'smv'}
                />
              </div>
              <div className={`form-group${targetMode !== 'smv' ? ' field-disabled' : ''}`}>
                <label>SMV (Standard Minute Value)</label>
                <input
                  type="number"
                  value={attributes.smv ?? ''}
                  onChange={(e) => setAttributes({ ...attributes, smv: parseFloat(e.target.value) })}
                  min="0"
                  step="0.1"
                  disabled={targetMode !== 'smv'}
                />
              </div>
              <div className={`form-group${targetMode !== 'direct' ? ' field-disabled' : ''}`}>
                <label>Target per Hour (pieces)</label>
                <input
                  type="number"
                  value={targetMode === 'smv' ? computedTargetPerHour : attributes.target_per_hour}
                  onChange={(e) => setAttributes({ ...attributes, target_per_hour: parseFloat(e.target.value) })}
                  min="0"
                  step="1"
                  disabled={targetMode !== 'direct'}
                />
                {targetMode === 'smv' && (
                  <small className="field-hint">
                    Calculated from SMV and operators: 60 ÷ {attributes.smv || 0} × {attributes.plannedOperators || 0} ={' '}
                    <b>{computedTargetPerHour}</b> pcs/hour
                  </small>
                )}
              </div>
              <div className="form-group">
                <label>Operations</label>
                <input
                  type="number"
                  value={attributes.operations}
                  onChange={(e) => setAttributes({ ...attributes, operations: parseInt(e.target.value) })}
                  min="1"
                  step="1"
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowSettings(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-success">
                  Update Attributes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Parameter Mapping Modal (Admin Only) */}
      {showParamMapping && isAdmin && (
        <div className="modal-overlay" onClick={() => setShowParamMapping(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Sliders size={20} /> Configure Parameter Mappings</h2>
              <button className="modal-close" onClick={() => setShowParamMapping(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleSaveParamMappings} className="modal-body">
              <p className="form-help">
                Map dashboard components to actual ThingsBoard telemetry keys from your devices.
              </p>
              
              <div className="param-mapping-grid">
                <div className="param-mapping-item">
                  <label>Efficiency</label>
                  <input
                    type="text"
                    value={paramMappings.efficiency}
                    onChange={(e) => setParamMappings({ ...paramMappings, efficiency: e.target.value })}
                    placeholder="e.g., efficiency"
                  />
                </div>
                
                <div className="param-mapping-item">
                  <label>Hourly PCS (Production Count)</label>
                  <input
                    type="text"
                    value={paramMappings.hourly_pcs}
                    onChange={(e) => setParamMappings({ ...paramMappings, hourly_pcs: e.target.value })}
                    placeholder="e.g., prod_cnt"
                  />
                </div>
                
                <div className="param-mapping-item">
                  <label>Total PCS</label>
                  <input
                    type="text"
                    value={paramMappings.total_pcs}
                    onChange={(e) => setParamMappings({ ...paramMappings, total_pcs: e.target.value })}
                    placeholder="e.g., total_pcs"
                  />
                </div>
                
                <div className="param-mapping-item">
                  <label>Downtime (Minutes)</label>
                  <input
                    type="text"
                    value={paramMappings.downtime}
                    onChange={(e) => setParamMappings({ ...paramMappings, downtime: e.target.value })}
                    placeholder="e.g., downtime"
                  />
                </div>
                
                <div className="param-mapping-item">
                  <label>Machine Breakdown</label>
                  <input
                    type="text"
                    value={paramMappings.machine_breakdown}
                    onChange={(e) => setParamMappings({ ...paramMappings, machine_breakdown: e.target.value })}
                    placeholder="e.g., machine_breakdown"
                  />
                </div>
                
                <div className="param-mapping-item">
                  <label>No Material</label>
                  <input
                    type="text"
                    value={paramMappings.no_material}
                    onChange={(e) => setParamMappings({ ...paramMappings, no_material: e.target.value })}
                    placeholder="e.g., no_material"
                  />
                </div>
                
                <div className="param-mapping-item">
                  <label>Power Issue</label>
                  <input
                    type="text"
                    value={paramMappings.power}
                    onChange={(e) => setParamMappings({ ...paramMappings, power: e.target.value })}
                    placeholder="e.g., power"
                  />
                </div>
                
                <div className="param-mapping-item">
                  <label>Quality Issue</label>
                  <input
                    type="text"
                    value={paramMappings.quality_issue}
                    onChange={(e) => setParamMappings({ ...paramMappings, quality_issue: e.target.value })}
                    placeholder="e.g., quality_issue"
                  />
                </div>
                
                <div className="param-mapping-item">
                  <label>Present Operators</label>
                  <input
                    type="text"
                    value={paramMappings.present_operators}
                    onChange={(e) => setParamMappings({ ...paramMappings, present_operators: e.target.value })}
                    placeholder="e.g., present_operators"
                  />
                </div>
                
                <div className="param-mapping-item">
                  <label>Hourly Production (Chart Data)</label>
                  <input
                    type="text"
                    value={paramMappings.hourly_production}
                    onChange={(e) => setParamMappings({ ...paramMappings, hourly_production: e.target.value })}
                    placeholder="e.g., prod_cnt"
                  />
                </div>
                
                <div className="param-mapping-item">
                  <label>Status (Line Status - 'idle' or 'stopped')</label>
                  <input
                    type="text"
                    value={paramMappings.status}
                    onChange={(e) => setParamMappings({ ...paramMappings, status: e.target.value })}
                    placeholder="e.g., status"
                  />
                </div>
                
                <div className="param-mapping-item">
                  <label>Idle Count</label>
                  <input
                    type="text"
                    value={paramMappings.idle_count}
                    onChange={(e) => setParamMappings({ ...paramMappings, idle_count: e.target.value })}
                    placeholder="e.g., idle_count"
                  />
                </div>
                
                <div className="param-mapping-item">
                  <label>Stopped Count</label>
                  <input
                    type="text"
                    value={paramMappings.stopped_count}
                    onChange={(e) => setParamMappings({ ...paramMappings, stopped_count: e.target.value })}
                    placeholder="e.g., stopped_count"
                  />
                </div>
              </div>
              
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowParamMapping(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Mappings
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionDashboard;
